import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { StyleSheet, ScrollView, ActivityIndicator, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore, useModelStore, useRouteStore, useModalStore, useChatStore, useAuthStore, useToastStore, useCompanyStore, useChatInputStore } from '@/store';
import { useLocalSearchParams, useRouter } from 'expo-router';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import ChatInput from '@/components/ChatInput/ChatInput';
import socketManager from '@/lib/socket';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import MetaHead from '@/components/MetaHead';
import ModelChatIntro from '@/components/Models/ModelChatIntro';
import SupportChatIntro from '@/components/Chat/SupportChatIntro';
import { buildSupportChatMessage } from '@/lib/supportChat';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import StatusContainer from '@/components/StatusContainer';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import ModelExploreShowcase from '@/components/Explore/ModelExploreShowcase';
import { canAccessPlusOnlyModel, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';

const loadingStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const createStyles = (isXS, colors, chatInputStyle, messageWidth, bottomInset = 0, keyboardHeight = 0) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  centeredContainer: {
    flex: 1,
    width: '100%',
    minWidth: 200,
    maxWidth: '60%',
    marginHorizontal: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
  },
  messagesContainer: {
    flex: 1,
    position: 'relative',
    paddingBottom: 0,
  },
  messagesContentContainer: {
    flexGrow: 1,
    justifyContent: isXS ? 'flex-start' : 'center',
    alignItems: 'center',
  },
  emptyStateContainer: {
    alignItems: 'center',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : MESSAGE_WIDTH_AMOUNTS.MEDIUM,
    width: '100%',
    paddingHorizontal: isXS ? 24 : 32,
    paddingVertical: isXS ? 24 : 40,
  },
  showcaseSlot: {
    width: '100%',
    marginTop: 32,
  },
  inputContainer: {
    width: '100%',
    zIndex: 2,
    position: isXS ? 'absolute' : 'relative',
    bottom: isXS ? 0 : 'auto',
    left: isXS ? 0 : 'auto',
    right: isXS ? 0 : 'auto',
    backgroundColor: isXS ? colors.solid_color_primary : 'transparent',
    paddingBottom: Math.max(keyboardHeight, isXS ? bottomInset : 0),
  },
});

const ModelChatPage = React.memo(({ model, isSupportChat = false }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const supportLaunchRef = useRef(false);
  const [sendingSupportQuestion, setSendingSupportQuestion] = useState(null);
  const router = useRouter();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const messageWidth = useUIStore(state => state.messageWidth);
  const user = useAuthStore(state => state.user);
  const isPlus = user?.flags?.isPlus;
  const canUseGatedModels = canAccessPlusOnlyModel(user);
  const userId = useAuthStore(state => state.user?.id);
  const company = useCompanyStore(state => state.getCompanyById(model?.companyId));
  
  // Pending auto-send for music/image/video generation
  const pendingAutoSend = useChatInputStore(state => state.pendingAutoSend);
  const clearPendingAutoSend = useChatInputStore(state => state.clearPendingAutoSend);
  const autoSendProcessed = useRef(false);

  // Split store selectors to prevent unnecessary re-renders
  const isLoadingModel = useModelStore(state => state.isLoadingModel);
  const modelError = useModelStore(state => state.modelError);

  const { setCurrentModelId } = useRouteStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const createConversation = useChatStore(state => state.createConversation);
  const [inputValue, setInputValue] = useState('');
  const keyboardHeight = useComposerKeyboardHeight();
  const [inputContainerHeight, setInputContainerHeight] = useState(0);

  const handleInputContainerLayout = useCallback((event) => {
    const nextHeight = Math.ceil(event?.nativeEvent?.layout?.height || 0);
    setInputContainerHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  }, []);

  const styles = useMemo(() => createStyles(isXS, colors, chatInputStyle, messageWidth, insets.bottom, keyboardHeight), [isXS, colors, chatInputStyle, messageWidth, insets.bottom, keyboardHeight]);
  const messageContainerStyle = useMemo(() => {
    const staticPaddingBottom = isXS ? (chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 180 : 170) : 0;
    return [
      styles.messagesContainer,
      { marginBottom: isXS ? (inputContainerHeight || staticPaddingBottom) : 0 },
    ];
  }, [styles.messagesContainer, chatInputStyle, isXS, inputContainerHeight]);
  const memoizedModelIds = useMemo(() => [model?.id], [model?.id]);
  const mockConversationId = `${isSupportChat ? 'new-support' : 'new'}-${model?.id}`;

  // Set current model ID
  useEffect(() => {
    setCurrentModelId(model?.id);
    return () => setCurrentModelId(null);
  }, [model?.id, setCurrentModelId]);

  // Set the chat input context for this model page
  const setContext = useChatInputStore(state => state.setContext);
  useEffect(() => {
    if (model?.id) {
      setContext(mockConversationId);
    }
  }, [model?.id, mockConversationId, setContext]);

  const handleSendSuggestion = useCallback(async (suggestion) => {
    // Another mounted chat can own the global composer context after Back.
    // Target this draft explicitly, including repeated clicks on a suggestion.
    const composer = useChatInputStore.getState();
    composer.setContext(mockConversationId);
    composer.setText(suggestion);
    setInputValue(suggestion);
  }, [mockConversationId]);

  const handleSend = useCallback(async (formattedMessage) => {
    if (formattedMessage) {
      if (isSupportChat) {
        if (supportLaunchRef.current) return false;
        supportLaunchRef.current = true;
        setSendingSupportQuestion(formattedMessage.text);
      }
      let sent = false;
      try {
        if (!(await socketManager.requireConnection())) {
          return false;
        }

        // The server checks the live balance; cached credits must not block a Chat send.

        if (isModelLockedForUser(user, model)) {
          openModelAccessPaywall(openModal, {
            model,
            title: 'Access Premium AI Models',
            subtitle: 'To use this model, buy credits or upgrade to Plus.',
          });
          return false;
        }

        const conversation = await createConversation({
          userId: userId,
          formattedMessage: isSupportChat ? buildSupportChatMessage(formattedMessage, model.id) : formattedMessage,
        });

        // kick off inference
        setTimeout(() => {
          socketManager.emitKickConversation(userId, conversation.id);
        }, 250);

        // Pre-initialize the new conversation's context with the selected model
        // so it persists when redirecting to the conversation page.
        // Skip this when _skipModelTransfer is set (e.g., quick-create auto-sends from
        // Image/Video/Music buttons) - those are one-shot generations and should reset to Auto mode.
        if (model?.id && !formattedMessage._skipModelTransfer) {
          const chatInputStore = useChatInputStore.getState();
          chatInputStore.setContext(conversation.id);
          chatInputStore.setSelectedModels([model.id]);
        }

        // Redirect to the new conversation
        router.push(`/c/${conversation.id}`);
        sent = true;
        return true;
      } catch (error) {
        console.error('Error creating conversation:', error);
        if (error?.name === 'ApiError' && error?.message) {
          addToast(error.message, 'error', error?.data?.actions);
        } else {
          addToast(error.message || 'Failed to create conversation', 'error');
        }
        // Keep the composer text so the user can retry without retyping.
        return false;
      } finally {
        if (isSupportChat && !sent) {
          supportLaunchRef.current = false;
          setSendingSupportQuestion(null);
        }
      }
    }
    return false;
  }, [model?.id, router, createConversation, addToast, isPlus, canUseGatedModels, openModal, userId, isSupportChat]);

  const handleSupportSuggestion = useCallback(question => requireAuth(() => handleSend({
    text: question, parameters: {}, fileIds: [], agentIds: [], modelIds: [model.id],
  }), { source: 'help_arya_support' }), [requireAuth, handleSend, model?.id]);

  // Handle pending auto-send messages (e.g., from Music/Images/Videos page)
  useEffect(() => {
    // Only process if we have pending data, model is loaded, and we haven't processed yet
    if (!isSupportChat && pendingAutoSend && pendingAutoSend.text && model?.id && userId && !autoSendProcessed.current) {
      console.log('[ModelChatPage] Processing pendingAutoSend:', pendingAutoSend);
      autoSendProcessed.current = true;
      
      const timer = setTimeout(async () => {
        const pendingData = { ...pendingAutoSend };
        clearPendingAutoSend();
        
        try {
          // Preserve any parameters selected before navigation, such as image
          // aspect ratio, while forcing this model page's model id below.
          const stateFormattedMessage = useChatInputStore.getState().getFormattedMessage?.();
          const parameters = {
            ...(stateFormattedMessage?.parameters || {}),
            action: pendingData.action || stateFormattedMessage?.parameters?.action || 'create-image',
          };
          
          // Include model-specific parameters (e.g., description for music generation)
          // These need to be nested under modelParameters[modelId] to match getFormattedMessage structure
          if (model.id && pendingData.description) {
            parameters.modelParameters = {
              ...(parameters.modelParameters || {}),
              [model.id]: {
                ...(parameters.modelParameters?.[model.id] || {}),
                description: pendingData.description,
              }
            };
          }
          
          const formattedMessage = {
            text: pendingData.text,
            parameters,
            actions: [],
            fileIds: Array.isArray(pendingData.fileIds) ? pendingData.fileIds.filter(Boolean) : (stateFormattedMessage?.fileIds || []),
            agentIds: [],
            modelIds: [model.id], // Use the model from this page
            _skipModelTransfer: true, // Don't persist generation model to new conversation - reset to Auto
          };

          await handleSend(formattedMessage);
        } catch (error) {
          console.error('[ModelChatPage] Error auto-sending message:', error);
          addToast(error.message || 'Failed to send message', 'error');
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [pendingAutoSend, clearPendingAutoSend, model?.id, userId, handleSend, addToast, isSupportChat]);

  if (isLoadingModel) {
    return (
      <View style={styles.centeredContainer}>
        <ActivityIndicator size="large" color={colors.text_color_secondary} />
      </View>
    );
  }

  if (modelError && !model && !isLoadingModel) {
    return (
      <View style={styles.centeredContainer}>
        <StatusContainer
          type='error'
          title={modelError || 'Unable to load model'}
          actionText='Go Back to Models'
          onAction={() => router.push('/models')}
        /> 
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MetaHead
        title={model?.name ? `Chat with ${model.name}` : "Chat with Model"}
        description={model?.description || "Start a conversation with this AI model. Ask questions, get assistance, and explore its capabilities through natural dialogue."}
        canonical={model?.slug ? `/m/${model.slug}` : (model?.id ? `/m/${model.id}` : '/models')}
      />
      <ScrollView 
        style={messageContainerStyle}
        contentContainerStyle={styles.messagesContentContainer}
        showsVerticalScrollIndicator={true}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.emptyStateContainer}>
          {isSupportChat ? <SupportChatIntro onSuggestionPress={handleSupportSuggestion} sendingQuestion={sendingSupportQuestion} /> : <ModelChatIntro
            model={model}
            company={company}
            onSuggestionPress={handleSendSuggestion}
          />}

          {!isSupportChat && <View style={styles.showcaseSlot}>
            <ModelExploreShowcase model={model} variant="page" />
          </View>}
        </View>
      </ScrollView>

      <View
        style={styles.inputContainer}
        onLayout={handleInputContainerLayout}
      >
        <ChatInputContainer>
          <ChatInput
            key={mockConversationId}
            onSend={handleSend}
            placeholder={isSupportChat ? t('settings.help.aryaPlaceholder') : 'Type a message...'}
            forcePlaceholder={isSupportChat}
            withModels={!isSupportChat}
            hideAgentTags={isSupportChat}
            hideVoiceMode={isSupportChat}
            isLoading={!!sendingSupportQuestion}
            isDisabled={!!sendingSupportQuestion}
            value={inputValue}
            onChangeText={setInputValue}
            modelIds={memoizedModelIds}
            conversationId={mockConversationId}
          />
        </ChatInputContainer>
      </View>
    </View>
  );
});

function ModelChatRoute() {
  const { id: modelId, support } = useLocalSearchParams();
  const { colors } = useTheme();
  const getModelById = useModelStore(state => state.getModelById);
  const getModelBySlug = useModelStore(state => state.getModelBySlug);
  const fetchModelById = useModelStore(state => state.fetchModelById);

  // Get model from store. Re-subscribing to `models` keeps this reactive so the
  // page re-renders (and picks up the freshly-fetched model) once it lands.
  // The `/m/:id` route accepts either an id or a slug, so resolve both.
  const models = useModelStore(state => state.models);
  const model = models[modelId] || getModelById(modelId) || getModelBySlug(modelId);
  const [isWaitingForModel, setIsWaitingForModel] = useState(!model);

  // Model metadata is public — anonymous visitors can land on `/m/:id`
  // and browse the model header / starter prompts before deciding to
  // sign up. The send action itself is gated downstream by `ChatInput`.
  //
  // Keep this effect keyed on `modelId` only. Depending on `model` /
  // `isLoadingModel` made the effect re-run (and its cleanup fire) the moment
  // `fetchModelById` flipped `isLoadingModel`, which invalidated the in-flight
  // request's `cancelled` flag before it resolved — so `setIsWaitingForModel(false)`
  // never ran and the page was stuck on the loader (most visible on a hard
  // refresh with an empty store, e.g. logged-out visitors).
  useEffect(() => {
    let cancelled = false;

    const loadModel = async () => {
      if (getModelById(modelId) || getModelBySlug(modelId)) {
        setIsWaitingForModel(false);
        return;
      }
      setIsWaitingForModel(true);
      try {
        await fetchModelById(modelId);
      } catch (error) {
        console.error('Error loading model:', error);
      } finally {
        if (!cancelled) setIsWaitingForModel(false);
      }
    };

    loadModel();

    return () => {
      cancelled = true;
    };
  }, [modelId, fetchModelById, getModelById, getModelBySlug]);
  
  if (isWaitingForModel) {
    return (
      <View style={loadingStyles.container}>
        <ActivityIndicator size="large" color={colors.text_color_secondary} />
      </View>
    );
  }

  return (
    <ModelChatPage key={`${model?.id}-${support === '1'}`} model={model} isSupportChat={support === '1' && model?.slug === 'arya'} />
  );
}

export default React.memo(ModelChatRoute);
